import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';
import { useMounted } from './prototype';

/**
 * «جاباز» is rolled out to ONE doctor profile: every visitor of that profile sees it, and no other profile
 * changes. The test tools stay with the owner (see useJabazTester). Everything is decided on the client
 * (after mount), so server rendering and the shared caches of the profile stay exactly as they are
 * (docs/performance.md: withCSR stays user-independent).
 */
export const JABAZ_ROLLOUT_SLUGS: readonly string[] = ['دکتر-محمد-محمدی-125'];

/** Opening the profile once with `?jabaz=1` (or `?jabaz=0` to undo) marks this device as the owner's test device. */
const OPT_IN_KEY = 'jabaz-opt-in';

const decode = (slug: string) => {
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
};

export const isJabazRolloutSlug = (slug?: string | null) => !!slug && JABAZ_ROLLOUT_SLUGS.includes(decode(slug));

const readOptIn = () => {
  try {
    return window.localStorage.getItem(OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Device opted in (see OPT_IN_KEY). With `onSlug`, the `?jabaz=1|0` of the address is applied first —
 * and only on the rollout profile; on any other page the address is never read and nothing is written.
 */
export const useJabazOptIn = (onSlug?: string | null) => {
  const [optedIn, setOptedIn] = useState(false);
  useEffect(() => {
    if (onSlug !== undefined && !isJabazRolloutSlug(onSlug)) return;
    const param = new URLSearchParams(window.location.search).get('jabaz');
    try {
      if (onSlug !== undefined && param === '1') window.localStorage.setItem(OPT_IN_KEY, '1');
      if (onSlug !== undefined && param === '0') window.localStorage.removeItem(OPT_IN_KEY);
    } catch {
      // storage blocked: fall back to the account check only
    }
    setOptedIn(readOptIn());
  }, [onSlug]);
  return optedIn;
};

/**
 * Outside the profile («نوبت‌های من»): true only on a device that opted in or already holds a seat in a
 * rollout doctor's queue. Read straight from localStorage so nobody else even downloads the Jabaz code.
 */
export const useJabazOnDevice = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('jabaz-state');
      const slugs = Object.keys(JSON.parse(raw ?? '{}')?.state?.memberships ?? {});
      setOn(readOptIn() || slugs.some(isJabazRolloutSlug));
    } catch {
      setOn(readOptIn());
    }
  }, []);
  return on;
};

/**
 * On a doctor profile: Jabaz is on for every visitor of the rollout slug, and off everywhere else. Still
 * applies `?jabaz=1|0` there (test device, see useJabazTester). Decided after mount — never in SSR.
 */
export const useJabazOnProfile = (slug?: string | null) => {
  useJabazOptIn(slug ?? null);
  const mounted = useMounted();
  return mounted && isJabazRolloutSlug(slug);
};

/**
 * The test tools are the owner's only: logged in with the doctor's own account (`doctorUserId` is the
 * profile's user id, kept on the seat), or a device opted in with `?jabaz=1`. Other visitors never see them.
 */
export const useJabazTester = (doctorUserId?: string | number | null) => {
  const optedIn = useJabazOptIn();
  const userId = useUserInfoStore(state => state.info?.id);
  const isLogin = useUserInfoStore(state => state.isLogin);
  const isOwner = isLogin && !!userId && doctorUserId != null && doctorUserId !== '' && String(doctorUserId) === String(userId);
  // once the owner's own account is recognised (on the profile), this device keeps the tools on every page
  // — also for seats joined before the seat carried the doctor's id
  useEffect(() => {
    if (!isOwner) return;
    try {
      window.localStorage.setItem(OPT_IN_KEY, '1');
    } catch {
      // storage blocked: the account check still works where the doctor's id is known
    }
  }, [isOwner]);
  return isOwner || optedIn;
};
